
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface EarlyAccessFormData {
  name: string;
  email: string;
  company: string;
  referralSource: string;
  accountType: string;
  userRole: string;
  implementationTimeline: string;
}

export const useEarlyAccessForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<EarlyAccessFormData>({
    name: "",
    email: "",
    company: "",
    referralSource: "",
    accountType: "",
    userRole: "",
    implementationTimeline: ""
  });
  const { toast } = useToast();

  const updateFormField = (field: keyof EarlyAccessFormData, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleAccountTypeSelect = (type: string) => {
    updateFormField("accountType", type);
  };

  const handleSubmit = async (e: React.FormEvent, reCaptchaToken: string) => {
    e.preventDefault();
  
    // Validate account type selection
    if (!formData.accountType) {
      toast({
        title: "Please select an account type",
        description: "You must select either Individual Trader or Institutional Client to continue.",
        variant: "destructive",
      });
      return;
    }
  
    // Accept fallback token in development/testing
    if (reCaptchaToken === "fallback_token") {
      console.warn("Proceeding with fallback verification");
    }
  
    setIsLoading(true);
  
    try {
      // Insert the early access request into the database
      const { error, data } = await supabase
        .from('early_access_requests')
        .insert([{
          name: formData.name,
          email: formData.email,
          company: formData.company,
          referral_source: formData.referralSource,
          account_type: formData.accountType,
          user_role: formData.userRole,
          implementation_timeline: formData.implementationTimeline
        }])
        .select();
        
      if (error) {
        console.error("Database error:", error);
        throw error;
      }
      
      console.log("Successfully stored form submission:", data);
      
      // Call the edge function to send the email notification
      const { error: functionError } = await supabase.functions.invoke('send-early-access-notification', {
        body: {
          name: formData.name,
          email: formData.email,
          company: formData.company,
          referralSource: formData.referralSource,
          accountType: formData.accountType,
          userRole: formData.userRole,
          implementationTimeline: formData.implementationTimeline
        }
      });
      
      if (functionError) {
        console.error("Function error:", functionError);
        // Continue anyway since the data is saved in the database
      }
      
      toast({
        title: "Request Submitted!",
        description: "We'll be in touch soon with your early access details.",
      });
    } catch (error) {
      console.error("Error submitting request:", error);
      toast({
        title: "Error",
        description: "Failed to submit your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    isLoading,
    updateFormField,
    handleAccountTypeSelect,
    handleSubmit
  };
};
